import { useState } from "react";
import { IconPencil, IconPlus, IconTrash } from "@tabler/icons-react";
import moment from "moment/moment";
import { produce } from "immer";
import Button from "../UI/Button";
import IconButton from "../UI/IconButton";
import { useToast } from "../UI/Toast/useToast";
import Text from "../UI/Text";
import Title from "../UI/Title";
// @ts-expect-error pure js
import FormEvent from "./FormEvent";
// @ts-expect-error pure js
import useEvents from "../../hooks/use-events";
// @ts-expect-error pure js
import { useModal } from "../../hooks/use-modal.jsx";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
import type { CalendarEvent } from "../../models/calendarEvent";
import styles from "./Events.module.css";
import Card from "../UI/Card.tsx";

type EventFormValues = Omit<CalendarEvent, "id">;

export default function Events() {
  const { events, updateEvent, addEvent, deleteEvent } = useEvents() as {
    events: CalendarEvent[];
    updateEvent: (id: string, values: EventFormValues) => Promise<void>;
    addEvent: (values: EventFormValues) => Promise<void>;
    deleteEvent: (id: string) => Promise<void>;
  };
  const [eventToEdit, setEventToEdit] = useState<CalendarEvent>();
  const toast = useToast();
  const [FormModal, closeForm, openForm] = useModal();

  // submitHandler / deleteFromFormHandler sont appelés par FormEvent, qui les
  // enveloppe déjà dans un try/catch et affiche l'erreur dans sa propre modale.
  // Les erreurs doivent donc remonter : les capturer ici afficherait le message
  // derrière la modale ouverte, et fermerait le formulaire malgré l'échec.
  async function submitHandler(values: EventFormValues) {
    const sanitizedValues = produce(
      sanitizeValues(values) as EventFormValues,
      (draft) => {
        draft.debut = moment(draft.debut).format("YYYY-MM-DD HH:mm");
        draft.fin = moment(draft.fin).format("YYYY-MM-DD HH:mm");
      },
    );
    if (eventToEdit) {
      await updateEvent(eventToEdit.id, sanitizedValues);
    } else {
      await addEvent(sanitizedValues);
    }
    closeForm();
  }

  async function deleteFromFormHandler(eventId: string) {
    await deleteEvent(eventId);
    closeForm();
    setEventToEdit(undefined);
  }

  // Suppression directe depuis la liste : aucun formulaire pour porter
  // l'erreur, on la signale donc par un toast.
  async function deleteRowHandler(eventId: string) {
    try {
      await deleteEvent(eventId);
    } catch (error) {
      toast.error({
        title: "Impossible de supprimer l'évènement",
        description: handleAPIError(error),
      });
    }
  }

  return (
    <>
      <FormModal size="small">
        <FormEvent
          event={eventToEdit}
          onSubmit={submitHandler}
          onDelete={deleteFromFormHandler}
        />
      </FormModal>
      <div className={styles.container}>
        <Title as="h2" variant="deca" weight="bold">
          Mon agenda
        </Title>
        <div className={styles.eventList}>
          {events.length > 0 ? (
            events.map((event) => (
              <Card key={event.id} className={styles.eventCard}>
                <div className={styles.dateBadge}>
                  <Text
                    as="span"
                    variant="kilo"
                    weight="bold"
                    className={styles.dateNumber}
                  >
                    {moment(event.debut).format("DD")}
                  </Text>
                  <Text as="span" variant="deci" className={styles.dateDay}>
                    {moment(event.debut).format("ddd")}
                  </Text>
                </div>
                <div>
                  <Text weight="bold" className={styles.ellipsis}>
                    {event.subject}
                  </Text>
                  <Text color="muted" className={styles.ellipsis}>
                    {moment(event.debut).format("HH:mm")} -{" "}
                    {moment(event.fin).format("HH:mm")}
                  </Text>
                </div>
                <div className={styles.eventActions}>
                  <IconButton
                    icon={IconPencil}
                    label="Modifier"
                    onClick={() => {
                      setEventToEdit(event);
                      openForm();
                    }}
                  />
                  <IconButton
                    icon={IconTrash}
                    label="Supprimer"
                    color="danger"
                    onClick={() => deleteRowHandler(event.id)}
                  />
                </div>
              </Card>
            ))
          ) : (
            <Text as="div" color="muted" className={styles.emptyState}>
              Aucun évènement à venir
            </Text>
          )}
        </div>
        <Button
          icon={IconPlus}
          onClick={() => {
            setEventToEdit(undefined);
            openForm();
          }}
        >
          Ajouter
        </Button>
      </div>
    </>
  );
}
